'use client';

import { useEffect, useRef, type ReactNode } from 'react';

type CallToActionStageProps = {
    children: ReactNode;
};

/** √3 — the height-to-half-base ratio of an equilateral triangle. */
const SQRT3 = 1.7320508;

/**
 * Where the outline's box starts, measured from the top of the whole closing
 * block — a flat 90px, exactly as the design has it, and deliberately not the
 * section's own padding-top. The outline therefore rides ~38px above the text
 * it traces, which is what gives its lower tip room to clear the footer's
 * column hairline instead of stopping just short of it.
 */
const OUTLINE_TOP = 90;

/**
 * The closing statement is revealed through a triangle that opens as the
 * section scrolls up: the signet's own shape, drawn once at page scale. A thin
 * outline traces the same triangle a beat ahead of the fill.
 *
 * Two deliberate choices about failure:
 *
 * - The clip is only ever applied from inside the scroll handler. Before it has
 *   run — no JS, an error, a crawler — the content sits there unclipped, rather
 *   than hidden behind a triangle of zero size.
 * - `prefers-reduced-motion` skips the whole effect, outline included.
 *
 * The hover play/pause for the line clips lives here too: they are `preload`ed
 * to their first frame and only decode while someone is actually looking at
 * them, which is what the design does.
 */
export function CallToActionStage({ children }: CallToActionStageProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    const outlineRef = useRef<SVGPolygonElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        const section = sectionRef.current;
        const inner = innerRef.current;
        if (!section || !inner) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let frame = 0;

        const update = () => {
            frame = 0;

            const sectionBox = section.getBoundingClientRect();
            const innerBox = inner.getBoundingClientRect();
            // The stage is nested inside the section's padded inner box, so the
            // block itself is what OUTLINE_TOP is measured from.
            const blockBox = (section.closest('.cta-section') ?? section).getBoundingClientRect();
            const viewport = window.innerHeight;

            // 0 while the section is still below the fold, 1 once it has risen
            // past the midpoint between viewport and section height — the whole
            // block's box, padding included, as the design measures it.
            const progress = Math.max(0, Math.min(1, (viewport - blockBox.top) / ((viewport + blockBox.height) / 2)));

            const width = innerBox.width;
            const height = innerBox.height;
            if (width === 0 || height === 0) return;

            // The triangle has to cover the whole box at progress 1, whichever
            // of the two dimensions is the binding one — hence the max, plus a
            // little margin so the edges clear the corners.
            const base = Math.max(width, height * SQRT3) * 1.15;
            const ratioX = base / width;
            const ratioY = (base * SQRT3) / (2 * height);

            const left = (50 - 50 * ratioX * progress).toFixed(2);
            const top = (50 - ((ratioY * 100) / 3) * progress).toFixed(2);
            const right = (50 + 50 * ratioX * progress).toFixed(2);
            const bottom = (50 + ((ratioY * 100 * 2) / 3) * progress).toFixed(2);

            inner.style.clipPath = `polygon(${left}% ${top}%, ${right}% ${top}%, 50% ${bottom}%)`;

            const outline = outlineRef.current;
            const svg = svgRef.current;
            if (outline && svg) {
                const centerX = width / 2;
                const centerY = height / 2;
                const triangleHeight = (base * SQRT3) / 2;

                const pointY = (centerY - (triangleHeight / 3) * progress).toFixed(1);
                const points = [
                    `${(centerX - (base / 2) * progress).toFixed(1)},${pointY}`,
                    `${(centerX + (base / 2) * progress).toFixed(1)},${pointY}`,
                    `${centerX.toFixed(1)},${(centerY + ((2 * triangleHeight) / 3) * progress).toFixed(1)}`,
                ].join(' ');

                outline.setAttribute('points', points);
                svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
                svg.style.left = `${innerBox.left - sectionBox.left}px`;
                svg.style.top = `${blockBox.top + OUTLINE_TOP - sectionBox.top}px`;
                svg.style.width = `${width}px`;
                svg.style.height = `${height}px`;
            }
        };

        const schedule = () => {
            if (frame) return;
            frame = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, []);

    const setClipsPlaying = (playing: boolean) => {
        innerRef.current?.querySelectorAll('video').forEach((video) => {
            if (playing) {
                void video.play().catch(() => {
                    // Autoplay can be refused (battery saver, iOS low-power);
                    // the poster frame stays, which is the same picture.
                });
            } else {
                video.pause();
            }
        });
    };

    return (
        <div className="cta-section__stage" ref={sectionRef}>
            <svg className="cta-section__outline" ref={svgRef} aria-hidden="true">
                <polygon
                    ref={outlineRef}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeOpacity="0.25"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>

            <div
                className="cta-section__inner"
                ref={innerRef}
                onMouseEnter={() => setClipsPlaying(true)}
                onMouseLeave={() => setClipsPlaying(false)}
            >
                {children}
            </div>
        </div>
    );
}
